<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick, inject } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { Capacitor } from '@capacitor/core'
import { useAuthStore } from '../stores/auth'
import conversationService from '../services/conversation.service'


// Props
interface Props {
  conversationUuid?: string
  showSidebar?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  showSidebar: true
})

// Emits
const emit = defineEmits<{
  close: []
  conversationRead: [conversationUuid: string]
  messageSent: [data: { conversationUuid: string, message: any }]
}>()

const router = useRouter()
const authStore = useAuthStore()
const { t } = useI18n()

// Messages container ref
const messagesContainer = ref<HTMLElement | null>(null)

// Keyboard height from Capacitor (injected from App.vue)
const keyboardHeight = inject<ReturnType<typeof ref<number>>>('keyboardHeight', ref(0))

// Read safe area inset value in pixels using CSS variable (respects platform overrides)
const getSafeAreaInset = (position: 'top' | 'bottom'): number => {
  const div = document.createElement('div')
  div.style.position = 'fixed'
  div.style.left = '0'
  div.style.width = '0'
  div.style.height = `var(--safe-area-${position})`
  document.body.appendChild(div)
  const value = div.offsetHeight
  document.body.removeChild(div)
  return value
}

// Dynamic height for messages container (mobile only, desktop uses flex-1)
const messagesContainerHeight = ref<string | undefined>('calc(100% - 268px)')
const calculateMessagesHeight = () => {
  // On desktop (768px+) don't set inline height — let md:flex-1 handle it
  if (window.innerWidth >= 768) {
    messagesContainerHeight.value = undefined
    return
  }
  const availableHeight = window.innerHeight
  const safeTop = getSafeAreaInset('top')
  const safeBottom = getSafeAreaInset('bottom')
  const kbHeight = keyboardHeight.value || 0
  const isAndroid = Capacitor.getPlatform() === 'android'
  if (kbHeight > 0) {
    // Keyboard open: on Android resize:'body' already shrinks innerHeight by keyboard,
    // so don't subtract kbHeight again — only subtract it on iOS/web
    const subtract = isAndroid ? 0 : kbHeight
    const h = availableHeight - 198 - safeTop - subtract
    messagesContainerHeight.value = h + 'px'
  } else {
    // Keyboard closed: full calculation
    const h = availableHeight - 268 - safeTop - safeBottom
    messagesContainerHeight.value = h + 'px'
  }
}

// Current conversation data from API
const currentConversation = ref<any>(null)
const isLoadingConversation = ref(false)

// Load conversation details
const loadConversation = async () => {
  if (!props.conversationUuid) return

  try {
    isLoadingConversation.value = true
    const clubUuid = authStore.userInitData?.club?.uuid

    if (!clubUuid) {
      console.error('Club UUID not found')
      return
    }

    const response = await conversationService.getConversation(clubUuid, props.conversationUuid)
    currentConversation.value = response.conversation
  } catch (error) {
    console.error('Error loading conversation:', error)
  } finally {
    isLoadingConversation.value = false
  }
}

// Mark conversation as read
const markConversationAsRead = async () => {
  if (!props.conversationUuid) return

  // Don't mark as read if there are no messages
  if (apiMessages.value.length === 0) return

  try {
    const clubUuid = authStore.userInitData?.club?.uuid
    if (!clubUuid) return

    await conversationService.markAsRead(clubUuid, props.conversationUuid)

    // Update local conversation list to mark as read
    const conversation = allConversations.value.find(conv => conv.uuid === props.conversationUuid)
    if (conversation) {
      conversation.is_unread = false
    }

    // Emit event to parent component to update its conversation list
    emit('conversationRead', props.conversationUuid)

    // Trigger global event to refresh unread count in navigation
    window.dispatchEvent(new Event('conversationMarkedAsRead'))
  } catch (error) {
    console.error('Error marking conversation as read:', error)
  }
}

// Conversations list from API
const allConversations = ref<any[]>([])
const isLoadingConversations = ref(false)

// Load conversations list
const loadConversationsList = async () => {
  try {
    isLoadingConversations.value = true
    const clubUuid = authStore.userInitData?.club?.uuid

    if (!clubUuid) {
      console.error('Club UUID not found')
      return
    }

    const response = await conversationService.getConversations(clubUuid, {
      limit: 50,
      offset: 0
    })

    // Format conversations for display
    allConversations.value = response.conversations.map((conv: any) => {
      let lastMessageText = ''
      if (conv.last_message) {
        const senderName = conv.last_message.sender
          ? `${conv.last_message.sender.first_name} ${conv.last_message.sender.last_name}`
          : ''

        if (conv.is_group && senderName) {
          lastMessageText = `${senderName}: ${conv.last_message.content}`
        } else {
          lastMessageText = conv.last_message.content
        }
      }

      return {
        uuid: conv.uuid,
        name: conv.name,
        is_group: conv.is_group,
        is_unread: conv.is_unread,
        lastMessage: lastMessageText,
        time: formatConversationTime(conv.last_message_at),
        last_message_at: conv.last_message_at
      }
    })
  } catch (error) {
    console.error('Error loading conversations:', error)
  } finally {
    isLoadingConversations.value = false
  }
}

// Format conversation time
const formatConversationTime = (timestamp: string | null) => {
  if (!timestamp) return ''

  const date = new Date(timestamp)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)

  if (diffMins < 1) return t('conversations.timeNow')
  if (diffMins < 60) return t('conversations.timeMinAgo', { minutes: diffMins })
  if (diffMins < 1440) {
    const hours = Math.floor(diffMins / 60)
    return t('conversations.timeHourAgo', { count: hours })
  }

  const days = Math.floor(diffMins / 1440)
  if (days < 7) return t('conversations.timeDaysAgo', { count: days })

  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  return `${day}.${month}`
}

// All mock messages for individual conversation (for infinite scroll)
const allMessagesIndividual = [
  // Older messages
  { sender: 'Anna Kowalska', message: 'Dzień dobry! Czy mogę zapisać się na treningi?', time: '20.12.2025 14:20', isMe: false, type: 'text' },
  { sender: 'Ja', message: 'Dzień dobry! Oczywiście, proszę wypełnić formularz zgłoszeniowy.', time: '20.12.2025 14:25', isMe: true, type: 'text' },
  { sender: 'Anna Kowalska', message: 'Już wysłałam formularz. Kiedy mogę zacząć?', time: '20.12.2025 15:10', isMe: false, type: 'text' },
  { sender: 'Ja', message: 'Świetnie! Może Pani zacząć już w tym tygodniu.', time: '20.12.2025 15:15', isMe: true, type: 'text' },
  { sender: 'Anna Kowalska', message: 'Wspaniale! Dziękuję bardzo 😊', time: '20.12.2025 15:20', isMe: false, type: 'text' },
  { sender: 'Ja', message: 'Do zobaczenia na treningu!', time: '20.12.2025 15:22', isMe: true, type: 'text' },
  // More messages
  { sender: 'Anna Kowalska', message: 'Witam! Mam pytanie dotyczące harmonogramu.', time: '22.12.2025 09:30', isMe: false, type: 'text' },
  { sender: 'Ja', message: 'Dzień dobry! Proszę pytać.', time: '22.12.2025 09:35', isMe: true, type: 'text' },
  { sender: 'Anna Kowalska', message: 'Czy w przyszłym tygodniu będą treningi?', time: '22.12.2025 09:40', isMe: false, type: 'text' },
  { sender: 'Ja', message: 'Tak, treningi odbywają się zgodnie z harmonogramem.', time: '22.12.2025 09:45', isMe: true, type: 'text' },
  // Current messages
  { sender: 'Anna Kowalska', message: 'Dzień dobry! Czy mógłby Pan mi powiedzieć o szczegółach jutrzejszego treningu?', time: '23.12.2025 10:15', isMe: false, type: 'text' },
  { sender: 'Ja', message: 'Dzień dobry! Oczywiście. Trening odbędzie się jutro o godzinie 17:00 na sali głównej.', time: '23.12.2025 10:18', isMe: true, type: 'text' },
  { sender: 'Anna Kowalska', message: 'Świetnie, dziękuję! Czy powinnam przynieść jakieś dodatkowe materiały?', time: '23.12.2025 10:20', isMe: false, type: 'text' },
  { sender: 'Ja', message: 'Nie, wszystko będzie na miejscu. Wystarczy strój sportowy i dobre nastawienie 😊', time: '23.12.2025 10:22', isMe: true, type: 'text' },
  { sender: 'Anna Kowalska', message: 'Wysyłam zdjęcie z ostatniego treningu', time: '23.12.2025 10:25', isMe: false, type: 'image', attachment: { url: 'https://images.unsplash.com/photo-1461896836934-ffe607ba8211?w=400', name: 'trening_grudzien.jpg' } },
  { sender: 'Ja', message: 'Świetne zdjęcie! Tutaj jest plan treningowy na najbliższy miesiąc', time: '23.12.2025 10:28', isMe: true, type: 'document', attachment: { name: 'Plan_treningowy_styczen_2025.pdf', size: '245 KB', extension: 'pdf' } },
  { sender: 'Anna Kowalska', message: 'Dziękuję! Wysyłam też dokumentację medyczną', time: '23.12.2025 10:30', isMe: false, type: 'document', attachment: { name: 'Badania_lekarskie_Anna_Kowalska.docx', size: '128 KB', extension: 'docx' } },
  { sender: 'Ja', message: 'Otrzymałem, wszystko wygląda dobrze!', time: '23.12.2025 10:32', isMe: true, type: 'text' },
  { sender: 'Anna Kowalska', message: 'Dzięki za informację, będę na treningu!', time: '5 min temu', isMe: false, type: 'text' },
]

// Messages from API
const apiMessages = ref<any[]>([])
const isLoadingMessages = ref(false)
const hasMoreMessages = ref(false)
const totalMessagesCount = ref(0)

// Load messages from API
const loadMessages = async (beforeUuid?: string) => {
  if (!props.conversationUuid) return

  try {
    isLoadingMessages.value = true
    const clubUuid = authStore.userInitData?.club?.uuid

    if (!clubUuid) {
      console.error('Club UUID not found')
      return
    }

    const params: any = {
      limit: 25,
      offset: 0
    }

    if (beforeUuid) {
      params.before_uuid = beforeUuid
    }

    const response = await conversationService.getMessages(clubUuid, props.conversationUuid, params)

    if (beforeUuid) {
      // Infinite scroll - prepend older messages
      apiMessages.value = [...response.messages.reverse(), ...apiMessages.value]
    } else {
      // Initial load - set messages (reverse to show oldest first)
      apiMessages.value = response.messages.reverse()

      // Scroll to bottom after initial load
      nextTick(() => {
        if (messagesContainer.value) {
          messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
        }
      })
    }

    hasMoreMessages.value = response.has_more
    totalMessagesCount.value = response.total_count

  } catch (error) {
    console.error('Error loading messages:', error)
  } finally {
    isLoadingMessages.value = false
  }
}

// Format messages for display
const messages = computed(() => {
  const currentPersonUuid = authStore.userInitData?.persons?.current?.uuid

  return apiMessages.value.map((msg: any) => {
    const isMe = msg.sender_person_uuid === currentPersonUuid
    const senderName = isMe ? t('conversation.me') : `${msg.sender_first_name} ${msg.sender_last_name}`

    // Extract image URLs from attachments or direct imageUrls field
    const imageUrls = msg.attachments
      ? msg.attachments.filter((att: any) => att.attachment_type === 'image').map((att: any) => att.file_url)
      : (msg.imageUrls || msg.image_urls || [])

    // Extract document attachment
    const documentAttachment = msg.attachments
      ? msg.attachments.find((att: any) => att.attachment_type === 'document')
      : (msg.documentUrl ? {
          file_url: msg.documentUrl,
          file_name: msg.documentName,
          file_size: msg.documentSize,
          file_extension: msg.documentExtension
        } : null)

    return {
      uuid: msg.uuid,
      sender: senderName,
      message: msg.content,
      time: formatMessageTime(msg.created_at),
      isMe: isMe,
      type: msg.message_type,
      sender_person_uuid: msg.sender_person_uuid,
      created_at: msg.created_at,
      imageUrls: imageUrls,
      documentAttachment: documentAttachment
    }
  })
})

// Convert URLs in text to clickable links
const linkifyText = (text: string): string => {
  if (!text) return ''

  // First, escape HTML to prevent XSS
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')

  // Convert newlines to <br>
  let processed = escaped.replace(/\n/g, '<br>')

  // Regex to match URLs - stop at whitespace, <br>, or end of string
  const urlRegex = /(https?:\/\/[^\s<]+)/gi

  processed = processed.replace(urlRegex, (url) => {
    // Clean up the URL - remove trailing punctuation that might be part of sentence
    let cleanUrl = url.replace(/[.,;:!?]+$/, '')

    return `<a href="${cleanUrl}" target="_blank" rel="noopener noreferrer" class="underline hover:opacity-80">${cleanUrl}</a>`
  })

  return processed
}

// Format message time
const formatMessageTime = (timestamp: string) => {
  const date = new Date(timestamp)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)

  // Format hours and minutes
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')

  if (diffMins < 1) return t('conversation.timeNow')
  if (diffMins < 60) return t('conversation.timeMinAgo', { minutes: diffMins })

  // Check if message is from today, yesterday, or older
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)

  const messageDate = new Date(date)
  messageDate.setHours(0, 0, 0, 0)

  if (messageDate.getTime() === today.getTime()) {
    return `${hours}:${minutes}`
  } else if (messageDate.getTime() === yesterday.getTime()) {
    return t('conversation.timeYesterday', { time: `${hours}:${minutes}` })
  } else {
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = date.getFullYear()
    return t('conversation.timeDate', { date: `${year}.${month}.${day}`, time: `${hours}:${minutes}` })
  }
}

// Format document date
const formatDocumentDate = (timestamp: string) => {
  const date = new Date(timestamp)
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  return `${day}.${month}.${year} ${hours}:${minutes}`
}

// Loading state for older messages
const isLoadingOlderMessages = ref(false)
const lastLoadMessagesTimestamp = ref(0)

// Options dropdown
const showOptionsMenu = ref(false)

// Modal states
const showNewConversationModal = ref(false)
const showParticipantsModal = ref(false)
const showAddParticipantsModal = ref(false)
const showEditGroupNameModal = ref(false)
const showPhotosGalleryModal = ref(false)
const showDocumentsListModal = ref(false)
const showRemoveParticipantModal = ref(false)

// Message input
const messageInput = ref('')
const messageTextarea = ref<HTMLTextAreaElement | null>(null)
const messageSendError = ref('')
const selectedImages = ref<Array<{ file: File, url: string }>>([])
const imageInput = ref<HTMLInputElement | null>(null)
const selectedDocument = ref<{ file: File, name: string } | null>(null)
const documentInput = ref<HTMLInputElement | null>(null)
const isInputFocused = ref(false)

const handleInputFocus = () => {
  isInputFocused.value = true
}

const handleInputBlur = () => {
  // Small delay to allow + button click to register before hiding
  setTimeout(() => {
    isInputFocused.value = false
  }, 150)
}

const handlePlusClick = () => {
  isInputFocused.value = false
  messageTextarea.value?.blur()
}

// New conversation modal data
const newConvSearchQuery = ref('')
const newConvSelectedUsers = ref<number[]>([])
const newConvGroupName = ref('')
const newConvGroupNameError = ref(false)

// Participants from API
const participants = computed(() => {
  return currentConversation.value?.participants || []
})

// Mock participants (for sidebar - will be removed later)
const mockParticipants = computed(() => {
  if (currentConversation.value?.isGroup) {
    return [
      { id: 1, name: 'Trener Mateusz', role: 'Admin', isMe: true },
      { id: 2, name: 'Jan Nowak', role: 'Uczestnik', isMe: false },
      { id: 3, name: 'Maria Kowalczyk', role: 'Uczestnik', isMe: false },
      { id: 4, name: 'Piotr Wiśniewski', role: 'Uczestnik', isMe: false },
      { id: 5, name: 'Tomasz Lewandowski', role: 'Uczestnik', isMe: false },
      { id: 6, name: 'Anna Nowak', role: 'Uczestnik', isMe: false },
      { id: 7, name: 'Katarzyna Mazur', role: 'Uczestnik', isMe: false },
    ]
  } else {
    return [
      { id: 1, name: 'Trener Mateusz', role: 'Admin', isMe: true },
      { id: 2, name: currentConversation.value?.name || '', role: 'Uczestnik', isMe: false },
    ]
  }
})

// Check if current user is admin
const isAdmin = computed(() => {
  return participants.value.some(p => p.isMe && p.role === 'Admin')
})

// Mock available users
const availableUsers = ref([
  { id: 1, name: 'Aleksandra Wójcik', role: 'Zawodnik' },
  { id: 2, name: 'Krzysztof Mazur', role: 'Trener' },
  { id: 3, name: 'Michał Dąbrowski', role: 'Zawodnik' },
  { id: 4, name: 'Paweł Jankowski', role: 'Zawodnik' },
  { id: 5, name: 'Natalia Krawczyk', role: 'Koordynator' },
  { id: 6, name: 'Wojciech Zieliński', role: 'Trener' },
  { id: 7, name: 'Magdalena Grabowska', role: 'Zawodnik' },
  { id: 8, name: 'Grzegorz Pawlak', role: 'Administrator' },
  { id: 9, name: 'Joanna Michalska', role: 'Koordynator' },
  { id: 10, name: 'Andrzej Król', role: 'Zawodnik' }
])

// Conversation photos from API
const conversationPhotos = ref<any[]>([])
const isLoadingPhotos = ref(false)
const totalPhotos = ref(0)
const photosOffset = ref(0)
const hasMorePhotos = ref(true)
const hasMoreNewerPhotos = ref(false)
const hasMoreOlderPhotos = ref(true)

// Gallery state
const currentPhotoIndex = ref(0)
const thumbnailContainer = ref<HTMLElement | null>(null)
const currentThumbnail = ref<HTMLElement | null>(null)

// Dynamic margin for last thumbnail to allow centering
// Returns margin value for a given thumbnail index
const getThumbnailMargin = (index: number) => {
  const totalPhotos = conversationPhotos.value.length
  if (totalPhotos <= 7) return '0'

  // Because of flex-row-reverse, the last photo in array (index = length-1) is FIRST visually (on the left)
  // We want to add left margin to it when viewing one of the last 7 photos
  const isLastPhoto = index === totalPhotos - 1
  if (!isLastPhoto) return '0'

  const photosFromEnd = totalPhotos - currentPhotoIndex.value - 1
  if (photosFromEnd < 7) {
    // Thumbnail width is 64px (w-16) + gap 8px (gap-2) = 72px per thumbnail
    const thumbnailWidth = 72
    const marginCount = 7 - photosFromEnd
    return `${marginCount * thumbnailWidth}px`
  }

  return '0'
}

// Load conversation photos with pagination
const loadConversationPhotos = async (limit: number = 15, offset: number = 0, append: boolean = false) => {
  if (!props.conversationUuid) return

  try {
    isLoadingPhotos.value = true
    const clubUuid = authStore.userInitData?.club?.uuid

    if (!clubUuid) {
      console.error('Club UUID not found')
      return
    }

    const response = await conversationService.getConversationImages(clubUuid, props.conversationUuid, {
      limit,
      offset
    })

    const newPhotos = response.images.map((img: any) => ({
      uuid: img.uuid,
      url: img.url,
      thumb: img.url, // Use same URL for thumbnail
      name: img.original_filename || img.display_name,
      messageUuid: img.message_uuid,
      conversationUuid: img.conversation_uuid,
      createdAt: img.created_at
    }))

    if (append) {
      conversationPhotos.value = [...conversationPhotos.value, ...newPhotos]
    } else {
      conversationPhotos.value = newPhotos
    }

    totalPhotos.value = response.total
    photosOffset.value = Number(response.offset) + response.images.length
    hasMorePhotos.value = response.has_more
  } catch (error) {
    console.error('Error loading conversation photos:', error)
  } finally {
    isLoadingPhotos.value = false
  }
}

// Load more older photos (lazy loading older images)
const loadMoreOlderPhotos = async (count: number = 1) => {
  if (!hasMoreOlderPhotos.value || isLoadingPhotos.value || !props.conversationUuid) return

  try {
    isLoadingPhotos.value = true
    const clubUuid = authStore.userInitData?.club?.uuid
    if (!clubUuid) return

    // Get the oldest (last) image we have
    const oldestImage = conversationPhotos.value[conversationPhotos.value.length - 1]

    // Fetch older images using before_timestamp (created_at < timestamp)
    const response = await conversationService.getConversationImages(clubUuid, props.conversationUuid, {
      limit: count,
      before_timestamp: oldestImage.createdAt
    })

    const newPhotos = response.images.map((img: any) => ({
      uuid: img.uuid,
      url: img.url,
      thumb: img.url,
      name: img.original_filename || img.display_name,
      messageUuid: img.message_uuid,
      conversationUuid: img.conversation_uuid,
      createdAt: img.created_at
    }))

    if (newPhotos.length > 0) {
      // Add new older photos to the end
      conversationPhotos.value = [...conversationPhotos.value, ...newPhotos]

      // Remove the same number of photos from the beginning (newest) to maintain max 15
      const maxPhotos = 15
      if (conversationPhotos.value.length > maxPhotos) {
        const photosToRemove = conversationPhotos.value.length - maxPhotos
        conversationPhotos.value = conversationPhotos.value.slice(photosToRemove)
        // Adjust current index because we removed photos from the start
        currentPhotoIndex.value = Math.max(0, currentPhotoIndex.value - photosToRemove)
        // Update flag - if we removed photos from start, there might be more newer photos
        hasMoreNewerPhotos.value = true
      }
    }

    hasMoreOlderPhotos.value = newPhotos.length === count
  } catch (error) {
    console.error('Error loading more older photos:', error)
  } finally {
    isLoadingPhotos.value = false
  }
}

// Load more newer photos (lazy loading newer images - prepend to array)
const loadMoreNewerPhotos = async (count: number = 1) => {
  if (!hasMoreNewerPhotos.value || isLoadingPhotos.value || !props.conversationUuid) return

  try {
    isLoadingPhotos.value = true
    const clubUuid = authStore.userInitData?.club?.uuid
    if (!clubUuid) return

    // Get the newest (first) image we currently have
    const newestImage = conversationPhotos.value[0]

    // Fetch newer images using after_timestamp (created_at > timestamp)
    const response = await conversationService.getConversationImages(clubUuid, props.conversationUuid, {
      limit: count,
      after_timestamp: newestImage.createdAt
    })

    const newPhotos = response.images.map((img: any) => ({
      uuid: img.uuid,
      url: img.url,
      thumb: img.url,
      name: img.original_filename || img.display_name,
      messageUuid: img.message_uuid,
      conversationUuid: img.conversation_uuid,
      createdAt: img.created_at
    }))

    if (newPhotos.length > 0) {
      // Prepend new newer photos to the beginning
      conversationPhotos.value = [...newPhotos, ...conversationPhotos.value]
      // Adjust current index because we prepended photos
      currentPhotoIndex.value += newPhotos.length

      // Remove the same number of photos from the end (oldest) to maintain max 15
      const maxPhotos = 15
      if (conversationPhotos.value.length > maxPhotos) {
        const photosToRemove = conversationPhotos.value.length - maxPhotos
        conversationPhotos.value = conversationPhotos.value.slice(0, maxPhotos)
        // Update flag - if we removed photos from end, there might be more older photos
        hasMoreOlderPhotos.value = true
      }
    }

    // Check if there are still more newer photos
    hasMoreNewerPhotos.value = newPhotos.length === count
  } catch (error) {
    console.error('Error loading more newer photos:', error)
  } finally {
    isLoadingPhotos.value = false
  }
}

// Documents
const displayedDocuments = ref<any[]>([])
const isLoadingDocuments = ref(false)
const documentsPage = ref(0)
const documentsPerPage = 10
const totalDocuments = ref(0)
const hasMoreDocuments = ref(false)

// Participant to remove
const participantToRemove = ref<{ uuid: string; name: string } | null>(null)

// Toggle options menu
const toggleOptionsMenu = () => {
  showOptionsMenu.value = !showOptionsMenu.value
}

// Auto-resize textarea (max 3 lines)
const adjustTextareaHeight = () => {
  if (!messageTextarea.value) return

  // Reset height to auto to get the correct scrollHeight
  messageTextarea.value.style.height = 'auto'

  // Calculate line height (approximately 24px per line)
  const lineHeight = 24
  const maxLines = 3
  const maxHeight = lineHeight * maxLines

  // Set height based on content, but max 3 lines
  const scrollHeight = messageTextarea.value.scrollHeight
  if (scrollHeight <= maxHeight) {
    messageTextarea.value.style.height = scrollHeight + 'px'
    messageTextarea.value.style.overflowY = 'hidden'
  } else {
    messageTextarea.value.style.height = maxHeight + 'px'
    messageTextarea.value.style.overflowY = 'auto'
  }
}

// Handle textarea input
const handleMessageInput = () => {
  adjustTextareaHeight()
  // Clear error message when user starts typing
  if (messageSendError.value) {
    messageSendError.value = ''
  }
}

// Handle Enter key - Enter = send, Shift+Enter = new line (both mobile & desktop)
const handleMessageKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Enter' && !event.shiftKey) {
    event.preventDefault()
    sendMessage()
  }
}

// Handle image selection
const handleImageSelect = (event: Event) => {
  const input = event.target as HTMLInputElement
  const files = input.files

  if (!files) return

  // Check limit
  if (selectedImages.value.length + files.length > 10) {
    messageSendError.value = t('conversation.maxImages')
    return
  }

  Array.from(files).forEach(file => {
    // Check allowed image types
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/heic', 'image/heif']
    if (!allowedTypes.includes(file.type)) {
      messageSendError.value = t('conversation.allowedImageFormats')
      return
    }

    // Check size (max 4MB)
    if (file.size > 4 * 1024 * 1024) {
      messageSendError.value = t('conversation.imageTooLarge')
      return
    }

    // Create preview URL
    const reader = new FileReader()
    reader.onload = (e) => {
      selectedImages.value.push({
        file: file,
        url: e.target?.result as string
      })

      // Focus on textarea after image is loaded
      nextTick(() => {
        messageTextarea.value?.focus()
      })
    }
    reader.readAsDataURL(file)
  })

  // Reset input
  if (input) {
    input.value = ''
  }
}

// Remove image from selection
const removeImage = (index: number) => {
  selectedImages.value.splice(index, 1)
}

// Clear all images
const clearAllImages = () => {
  selectedImages.value = []
}

// Handle document selection
const handleDocumentSelect = (event: Event) => {
  const input = event.target as HTMLInputElement
  const files = input.files

  if (!files || files.length === 0) return

  const file = files[0]

  // Validate file type
  const allowedTypes = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/xml',
    'application/xml'
  ]

  if (!allowedTypes.includes(file.type)) {
    messageSendError.value = t('conversation.invalidDocumentType')
    return
  }

  // Validate file size (5MB max)
  const maxSize = 5 * 1024 * 1024 // 5MB
  if (file.size > maxSize) {
    messageSendError.value = t('conversation.documentTooLarge')
    return
  }

  // Clear any images when document is selected
  selectedImages.value = []

  // Set selected document
  selectedDocument.value = {
    file: file,
    name: file.name
  }

  // Clear error and focus on message input
  messageSendError.value = ''
  nextTick(() => {
    messageTextarea.value?.focus()
  })

  // Reset input
  input.value = ''
}

// Remove selected document
const removeDocument = () => {
  selectedDocument.value = null
}

// Send message
const sendMessage = async () => {
  const messageContent = messageInput.value.trim()

  // Must have content, images or document
  if (!messageContent && selectedImages.value.length === 0 && !selectedDocument.value) return
  if (!props.conversationUuid) return

  // Clear any previous error
  messageSendError.value = ''

  const currentPersonUuid = authStore.userInitData?.persons?.current?.uuid
  const currentPerson = authStore.userInitData?.persons?.current

  // Determine message type
  let messageType = 'text'
  if (selectedImages.value.length > 0) messageType = 'image'
  if (selectedDocument.value) messageType = 'document'

  // Clear input and reset height immediately for better UX
  const contentToSend = messageContent
  const imagesToSend = [...selectedImages.value]
  const documentToSend = selectedDocument.value

  messageInput.value = ''
  selectedImages.value = []
  selectedDocument.value = null
  if (messageTextarea.value) {
    messageTextarea.value.style.height = 'auto'
    messageTextarea.value.style.overflowY = 'hidden'
  }

  // Add message to UI optimistically
  const newMessage = {
    uuid: `temp-${Date.now()}`,
    sender_person_uuid: currentPersonUuid,
    sender_first_name: currentPerson?.first_name || '',
    sender_last_name: currentPerson?.last_name || '',
    message_type: messageType,
    content: contentToSend,
    created_at: new Date().toISOString(),
    is_deleted: false
  }

  apiMessages.value.push(newMessage)

  // Scroll to bottom
  nextTick(() => {
    scrollToBottom()
  })

  // Send to API
  try {
    const clubUuid = authStore.userInitData?.club?.uuid

    if (!clubUuid) {
      console.error('Club UUID not found')
      messageSendError.value = t('conversation.sendError')
      return
    }

    // Create FormData if we have images or document
    let payload: any
    if (imagesToSend.length > 0) {
      payload = new FormData()
      payload.append('content', contentToSend)
      payload.append('message_type', 'image')

      imagesToSend.forEach((image) => {
        payload.append('images[]', image.file)
      })
    } else if (documentToSend) {
      payload = new FormData()
      payload.append('content', contentToSend)
      payload.append('message_type', 'document')
      payload.append('document', documentToSend.file)
    } else {
      payload = {
        content: contentToSend,
        message_type: 'text'
      }
    }

    const response = await conversationService.sendMessage(clubUuid, props.conversationUuid, payload)

    // Replace optimistic message with real message from API
    const tempMessageIndex = apiMessages.value.findIndex(m => m.uuid === newMessage.uuid)
    if (tempMessageIndex !== -1 && response.data) {
      apiMessages.value[tempMessageIndex] = response.data
    }

    // Emit event to update conversation list
    emit('messageSent', {
      conversationUuid: props.conversationUuid,
      message: {
        content: contentToSend,
        created_at: new Date().toISOString(),
        sender: {
          first_name: currentPerson?.first_name || '',
          last_name: currentPerson?.last_name || ''
        }
      }
    })

  } catch (error: any) {
    console.error('Error sending message:', error)
    messageSendError.value = error?.response?.data?.message || t('conversation.sendMessageError')

    // Remove optimistic message on error
    const tempMessageIndex = apiMessages.value.findIndex(m => m.uuid === newMessage.uuid)
    if (tempMessageIndex !== -1) {
      apiMessages.value.splice(tempMessageIndex, 1)
    }
  }
}

// === NEW CONVERSATION MODAL ===
const openNewConversationModal = () => {
  showNewConversationModal.value = true
  newConvSearchQuery.value = ''
  newConvSelectedUsers.value = []
  newConvGroupName.value = ''
  newConvGroupNameError.value = false
  showOptionsMenu.value = false
}

const closeNewConversationModal = () => {
  showNewConversationModal.value = false
  newConvSearchQuery.value = ''
  newConvSelectedUsers.value = []
  newConvGroupName.value = ''
  newConvGroupNameError.value = false
}

const filteredNewConvUsers = computed(() => {
  if (!newConvSearchQuery.value) return availableUsers.value
  const query = newConvSearchQuery.value.toLowerCase()
  return availableUsers.value.filter(user =>
    user.name.toLowerCase().includes(query) ||
    user.role.toLowerCase().includes(query)
  )
})

const isNewConvUserSelected = (userId: number) => {
  return newConvSelectedUsers.value.includes(userId)
}

const toggleNewConvUserSelection = (userId: number) => {
  const index = newConvSelectedUsers.value.indexOf(userId)
  if (index > -1) {
    newConvSelectedUsers.value.splice(index, 1)
  } else {
    newConvSelectedUsers.value.push(userId)
  }
}

const handleNewConvGroupNameInput = () => {
  if (newConvGroupNameError.value) {
    newConvGroupNameError.value = false
  }
}

const createNewConversation = () => {
  newConvGroupNameError.value = false
  if (newConvSelectedUsers.value.length === 0) {
    alert(t('conversation.selectAtLeastOne'))
    return
  }
  if (newConvSelectedUsers.value.length > 1 && !newConvGroupName.value.trim()) {
    newConvGroupNameError.value = true
    return
  }
  closeNewConversationModal()
  alert(t('conversation.featureComingSoon'))
}

// === PARTICIPANTS MODAL ===
const participantsList = ref<any[]>([])
const isLoadingParticipants = ref(false)
const participantsError = ref('')

// Check if current user is admin
const isCurrentUserAdmin = computed(() => {
  const currentPersonUuid = authStore.userInitData?.persons?.current?.uuid
  return participants.value.some(p => p.person_uuid === currentPersonUuid && p.is_admin)
})

const openParticipantsModal = async () => {
  showParticipantsModal.value = true
  showOptionsMenu.value = false
  participantsError.value = ''

  // Fetch participants from API
  if (!props.conversationUuid) return

  try {
    isLoadingParticipants.value = true
    const clubUuid = authStore.userInitData?.club?.uuid

    if (!clubUuid) {
      console.error('Club UUID not found')
      return
    }

    const response = await conversationService.getParticipants(clubUuid, props.conversationUuid)
    participantsList.value = response.participants
  } catch (error) {
    console.error('Error loading participants:', error)
  } finally {
    isLoadingParticipants.value = false
  }
}

const closeParticipantsModal = () => {
  showParticipantsModal.value = false
  participantsList.value = []
  participantsError.value = ''
}

// Check if participant is current user
const isCurrentUser = (personUuid: string) => {
  const currentPersonUuid = authStore.userInitData?.persons?.current?.uuid
  return personUuid === currentPersonUuid
}

// Get role translation
const getRoleTranslation = (role: string) => {
  return t(`roles.${role}`, role)
}

// Confirm remove participant
const confirmRemoveParticipant = (participantUuid: string, participantName: string) => {
  participantsError.value = '' // Clear any previous errors
  participantToRemove.value = {
    uuid: participantUuid,
    name: participantName
  }
  showRemoveParticipantModal.value = true
}

const closeRemoveParticipantModal = () => {
  showRemoveParticipantModal.value = false
  participantToRemove.value = null
}

// === ADD PARTICIPANTS MODAL ===
const addParticipantsSearchQuery = ref('')
const addParticipantsSelectedUsers = ref<string[]>([])
const availablePersonsForAdd = ref<any[]>([])
const isLoadingAvailablePersons = ref(false)
const addParticipantsError = ref('')
const isAddingParticipants = ref(false)

const openAddParticipantsModal = async () => {
  showAddParticipantsModal.value = true
  showOptionsMenu.value = false
  addParticipantsSearchQuery.value = ''
  addParticipantsSelectedUsers.value = []
  addParticipantsError.value = ''

  // Fetch available persons from new endpoint
  if (!props.conversationUuid) return

  try {
    isLoadingAvailablePersons.value = true
    const clubUuid = authStore.userInitData?.club?.uuid

    if (!clubUuid) {
      console.error('Club UUID not found')
      return
    }

    const response = await conversationService.getAvailablePersons(clubUuid, props.conversationUuid)
    availablePersonsForAdd.value = response.persons
  } catch (error) {
    console.error('Error loading available persons:', error)
    addParticipantsError.value = t('conversation.errorFetchingPersons')
  } finally {
    isLoadingAvailablePersons.value = false
  }
}

const closeAddParticipantsModal = () => {
  showAddParticipantsModal.value = false
  addParticipantsSearchQuery.value = ''
  addParticipantsSelectedUsers.value = []
  availablePersonsForAdd.value = []
  addParticipantsError.value = ''
}

const filteredAvailablePersons = computed(() => {
  if (!addParticipantsSearchQuery.value) return availablePersonsForAdd.value
  const query = addParticipantsSearchQuery.value.toLowerCase()
  return availablePersonsForAdd.value.filter((person: any) =>
    person.first_name.toLowerCase().includes(query) ||
    person.last_name.toLowerCase().includes(query) ||
    getRoleTranslation(person.role).toLowerCase().includes(query)
  )
})

const isPersonSelectedForAdd = (personUuid: string) => {
  return addParticipantsSelectedUsers.value.includes(personUuid)
}

const togglePersonSelectionForAdd = (personUuid: string) => {
  const index = addParticipantsSelectedUsers.value.indexOf(personUuid)
  if (index > -1) {
    addParticipantsSelectedUsers.value.splice(index, 1)
  } else {
    addParticipantsSelectedUsers.value.push(personUuid)
  }
}

const addParticipantsToGroup = async () => {
  if (addParticipantsSelectedUsers.value.length === 0) {
    addParticipantsError.value = t('conversation.selectAtLeastOne')
    return
  }

  if (!props.conversationUuid) return

  try {
    isAddingParticipants.value = true
    addParticipantsError.value = ''
    const clubUuid = authStore.userInitData?.club?.uuid

    if (!clubUuid) {
      console.error('Club UUID not found')
      return
    }

    await conversationService.addParticipants(clubUuid, props.conversationUuid, {
      participant_uuids: addParticipantsSelectedUsers.value
    })

    // Refresh participants list
    const response = await conversationService.getParticipants(clubUuid, props.conversationUuid)
    participantsList.value = response.participants

    // Close modal
    closeAddParticipantsModal()
  } catch (error: any) {
    console.error('Error adding participants:', error)
    const errorMessage = error.response?.data?.message || t('conversation.errorAddingParticipants')
    addParticipantsError.value = errorMessage
  } finally {
    isAddingParticipants.value = false
  }
}

// === EDIT GROUP NAME MODAL ===
const editGroupName = ref(currentConversation.value?.name || '')
const editGroupNameError = ref('')
const isSavingGroupName = ref(false)

const openEditGroupNameModal = () => {
  editGroupName.value = currentConversation.value?.name || ''
  editGroupNameError.value = ''
  showEditGroupNameModal.value = true
  showOptionsMenu.value = false
}

const closeEditGroupNameModal = () => {
  showEditGroupNameModal.value = false
  editGroupNameError.value = ''
}

const saveGroupName = async () => {
  if (!editGroupName.value.trim()) {
    editGroupNameError.value = t('conversation.groupNameEmpty')
    return
  }

  if (!props.conversationUuid) return

  try {
    isSavingGroupName.value = true
    editGroupNameError.value = ''
    const clubUuid = authStore.userInitData?.club?.uuid

    if (!clubUuid) {
      console.error('Club UUID not found')
      return
    }

    const response = await conversationService.updateGroupName(clubUuid, props.conversationUuid, {
      name: editGroupName.value.trim()
    })

    // Update current conversation name
    if (currentConversation.value) {
      currentConversation.value.name = response.conversation.name
    }

    // Reload conversation to get updated data
    await loadConversation()

    closeEditGroupNameModal()
  } catch (error: any) {
    console.error('Error updating group name:', error)
    const errorMessage = error.response?.data?.message || t('conversation.errorSavingGroupName')
    editGroupNameError.value = errorMessage
  } finally {
    isSavingGroupName.value = false
  }
}

// === PHOTOS GALLERY MODAL ===
const downloadDocument = async (url: string, fileName: string) => {
  try {
    const response = await fetch(url)
    const blob = await response.blob()
    const objectUrl = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = objectUrl
    link.download = fileName
    link.click()
    URL.revokeObjectURL(objectUrl)
  } catch (error) {
    console.error('Error downloading document:', error)
  }
}

const openPhotosGallery = async () => {
  showOptionsMenu.value = false
  await loadConversationPhotos()
  currentPhotoIndex.value = 0 // Start with the newest photo (first in array)
  showPhotosGalleryModal.value = true
  scrollThumbnailToCenter()
}

// Open gallery with specific image (from message click)
const openPhotosGalleryWithImage = async (imageUrl: string) => {
  try {
    isLoadingPhotos.value = true
    const clubUuid = authStore.userInitData?.club?.uuid

    if (!clubUuid || !props.conversationUuid) {
      console.error('Club UUID or Conversation UUID not found')
      return
    }

    // Extract image UUID from URL
    const urlParts = imageUrl.split('/')
    const imageUuid = urlParts[urlParts.length - 1]

    // Load 15 images centered on clicked image (7 before + clicked + 7 after)
    const response = await conversationService.getImagesAroundImage(
      clubUuid,
      props.conversationUuid,
      imageUuid,
      { before: 7, after: 7 }
    )

    conversationPhotos.value = response.images.map((img: any) => ({
      uuid: img.uuid,
      url: img.url,
      thumb: img.url,
      name: img.original_filename || img.display_name,
      messageUuid: img.message_uuid,
      conversationUuid: img.conversation_uuid,
      createdAt: img.created_at
    }))

    totalPhotos.value = response.total
    currentPhotoIndex.value = response.target_index

    // Check if we can load more in each direction
    hasMoreNewerPhotos.value = currentPhotoIndex.value > 0
    hasMoreOlderPhotos.value = (conversationPhotos.value.length + (response.total - conversationPhotos.value.length)) > conversationPhotos.value.length

    showPhotosGalleryModal.value = true
    scrollThumbnailToCenter()
  } catch (error) {
    console.error('Error loading images around clicked image:', error)
  } finally {
    isLoadingPhotos.value = false
  }
}

const closePhotosGallery = () => {
  showPhotosGalleryModal.value = false
}

const scrollThumbnailToCenter = () => {
  nextTick(() => {
    setTimeout(() => {
      if (!thumbnailContainer.value || !currentThumbnail.value) return

      const container = thumbnailContainer.value
      const thumbnail = currentThumbnail.value

      // Calculate scroll position manually because flex-row-reverse breaks scrollIntoView
      const containerRect = container.getBoundingClientRect()
      const thumbnailRect = thumbnail.getBoundingClientRect()

      // Calculate how much we need to scroll
      const thumbnailCenter = thumbnailRect.left + thumbnailRect.width / 2
      const containerCenter = containerRect.left + containerRect.width / 2
      const scrollOffset = thumbnailCenter - containerCenter

      // Scroll to center the thumbnail
      container.scrollBy({
        left: scrollOffset,
        behavior: 'smooth'
      })
    }, 50)
  })
}

const previousPhoto = async () => {
  if (currentPhotoIndex.value < conversationPhotos.value.length - 1) {
    currentPhotoIndex.value++

    // Load 1 more older photo when reaching position 8 from start (middle of 15)
    // This ensures we always have buffer on both sides
    if (currentPhotoIndex.value >= 7 && hasMoreOlderPhotos.value) {
      await loadMoreOlderPhotos(1)
    }

    scrollThumbnailToCenter()
  }
}

const nextPhoto = async () => {
  if (currentPhotoIndex.value > 0) {
    currentPhotoIndex.value--

    // Load 1 more newer photo when reaching position 7 from start (middle of 15)
    // This ensures we always have buffer on both sides
    if (currentPhotoIndex.value <= 7 && hasMoreNewerPhotos.value) {
      await loadMoreNewerPhotos(1)
    }

    // Scroll after potential loading
    scrollThumbnailToCenter()
  }
}

const goToPhoto = async (index: number) => {
  const targetIndex = index
  const currentLength = conversationPhotos.value.length

  // Save the UUID of the photo we want to navigate to
  const targetPhotoUuid = conversationPhotos.value[targetIndex]?.uuid

  // Check if we need to load newer photos (when clicking near the start)
  if (targetIndex <= 2 && hasMoreNewerPhotos.value) {
    const photosToLoad = 3 - targetIndex
    await loadMoreNewerPhotos(photosToLoad)

    // Find the photo by UUID after loading
    const newIndex = conversationPhotos.value.findIndex(p => p.uuid === targetPhotoUuid)
    if (newIndex !== -1) {
      currentPhotoIndex.value = newIndex
    } else {
      currentPhotoIndex.value = targetIndex
    }
    scrollThumbnailToCenter()
    return
  }

  // Check if we need to load older photos (when clicking near the end or beyond)
  if (targetIndex >= currentLength - 3 && hasMoreOlderPhotos.value) {
    const photosToLoad = Math.max(3, targetIndex - currentLength + 3)
    await loadMoreOlderPhotos(photosToLoad)

    // Find the photo by UUID after loading
    const newIndex = conversationPhotos.value.findIndex(p => p.uuid === targetPhotoUuid)
    if (newIndex !== -1) {
      currentPhotoIndex.value = newIndex
    } else {
      currentPhotoIndex.value = targetIndex
    }
    scrollThumbnailToCenter()
    return
  }

  // No loading needed, just go to the photo
  currentPhotoIndex.value = targetIndex
  scrollThumbnailToCenter()
}

const handleGalleryKeydown = (event: KeyboardEvent) => {
  if (!showPhotosGalleryModal.value) return
  if (event.key === 'ArrowLeft') previousPhoto()
  if (event.key === 'ArrowRight') nextPhoto()
  if (event.key === 'Escape') closePhotosGallery()
}

// === DOCUMENTS LIST MODAL ===
const openDocumentsList = async () => {
  showDocumentsListModal.value = true
  showOptionsMenu.value = false

  // Load first page
  displayedDocuments.value = []
  documentsPage.value = 0

  await loadDocuments()
}

const closeDocumentsList = () => {
  showDocumentsListModal.value = false
  displayedDocuments.value = []
  documentsPage.value = 0
  totalDocuments.value = 0
  hasMoreDocuments.value = false
}

const loadDocuments = async () => {
  if (isLoadingDocuments.value || !props.conversationUuid) return

  try {
    isLoadingDocuments.value = true
    const clubUuid = authStore.userInitData?.club?.uuid

    if (!clubUuid) return

    const offset = documentsPage.value * documentsPerPage
    const response = await conversationService.getConversationDocuments(clubUuid, props.conversationUuid, {
      limit: documentsPerPage,
      offset: offset
    })

    displayedDocuments.value.push(...response.documents)
    totalDocuments.value = response.total
    hasMoreDocuments.value = response.has_more
    documentsPage.value++
  } catch (error) {
    console.error('Error loading documents:', error)
  } finally {
    isLoadingDocuments.value = false
  }
}

const loadMoreDocuments = () => {
  if (!hasMoreDocuments.value || isLoadingDocuments.value) return
  loadDocuments()
}

const handleDocumentsScroll = (event: Event) => {
  const target = event.target as HTMLElement
  const scrollTop = target.scrollTop
  const scrollHeight = target.scrollHeight
  const clientHeight = target.clientHeight

  if (scrollTop + clientHeight >= scrollHeight - 100) {
    loadMoreDocuments()
  }
}

// === REMOVE PARTICIPANT MODAL ===
// (moved to participants modal section above)
const isRemovingParticipant = ref(false)

const removeParticipant = async () => {
  if (!participantToRemove.value || !props.conversationUuid) return

  try {
    isRemovingParticipant.value = true
    participantsError.value = ''
    const clubUuid = authStore.userInitData?.club?.uuid

    if (!clubUuid) {
      console.error('Club UUID not found')
      return
    }

    await conversationService.removeParticipant(
      clubUuid,
      props.conversationUuid,
      participantToRemove.value.uuid
    )

    // Refresh participants list
    const response = await conversationService.getParticipants(clubUuid, props.conversationUuid)
    participantsList.value = response.participants

    // Close remove confirmation modal
    closeRemoveParticipantModal()
  } catch (error: any) {
    console.error('Error removing participant:', error)

    // Show error message in participants modal
    const errorMessage = error.response?.data?.message || t('conversation.errorRemovingParticipant')
    participantsError.value = errorMessage

    // Close remove confirmation modal
    closeRemoveParticipantModal()
  } finally {
    isRemovingParticipant.value = false
  }
}

// Navigate to conversation list or emit close event
const goBack = () => {
  if (props.showSidebar) {
    router.push('/conversations')
  } else {
    emit('close')
  }
}

// Open conversation
const openConversation = (uuid: string) => {
  if (props.showSidebar) {
    router.push(`/conversations/${uuid}`)
  }
}

// Get avatar class
const getAvatarClass = (isGroup: boolean) => {
  return isGroup ? 'bg-green-500' : 'bg-blue-500'
}

// Get avatar content
const getAvatarContent = (name: string, isGroup: boolean) => {
  return isGroup ? '👥' : name.charAt(0).toUpperCase()
}

// Get document icon color
const getDocumentIconColor = (extension: string, isMe: boolean) => {
  if (extension === 'pdf') {
    return isMe ? 'text-red-300' : 'text-red-600'
  } else if (extension === 'docx') {
    return isMe ? 'text-blue-300' : 'text-blue-600'
  } else if (extension === 'xlsx') {
    return isMe ? 'text-green-300' : 'text-green-600'
  }
  return isMe ? 'text-gray-300' : 'text-gray-600'
}

// Close dropdown when clicking outside
const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as HTMLElement
  if (!target.closest('.options-menu')) {
    showOptionsMenu.value = false
  }
}

// Scroll to bottom of messages
const scrollToBottom = () => {
  nextTick(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
    }
  })
}

// Load older messages for infinite scroll
const loadOlderMessages = async () => {
  if (isLoadingOlderMessages.value || !hasMoreMessages.value || isLoadingMessages.value) {
    return
  }

  // Throttle: max 1 request per second
  const now = Date.now()
  if (now - lastLoadMessagesTimestamp.value < 1000) {
    return
  }
  lastLoadMessagesTimestamp.value = now

  isLoadingOlderMessages.value = true

  // Save current scroll position
  const container = messagesContainer.value
  const previousScrollHeight = container?.scrollHeight || 0

  // Get the oldest message UUID for before_uuid parameter
  const oldestMessage = apiMessages.value[0]
  if (!oldestMessage) {
    isLoadingOlderMessages.value = false
    return
  }

  try {
    await loadMessages(oldestMessage.uuid)

    // Restore scroll position after new messages are added
    nextTick(() => {
      if (container) {
        const newScrollHeight = container.scrollHeight
        container.scrollTop = newScrollHeight - previousScrollHeight
      }
    })
  } catch (error) {
    console.error('Error loading older messages:', error)
  } finally {
    isLoadingOlderMessages.value = false
  }
}

// Handle scroll event to load older messages
const handleMessagesScroll = (event: Event) => {
  const target = event.target as HTMLElement
  const scrollTop = target.scrollTop

  // Check if scrolled near the top (within 100px)
  if (scrollTop < 100 && !isLoadingOlderMessages.value && hasMoreMessages.value) {
    loadOlderMessages()
  }
}

// Recalculate messages height when keyboard opens/closes
watch(keyboardHeight, () => {
  // Small delay to let Capacitor finish resizing body
  setTimeout(() => {
    calculateMessagesHeight()
    nextTick(() => scrollToBottom())
  }, 50)
})

// Watch for conversation changes
watch(() => props.conversationUuid, async () => {
  // Load conversation details when UUID changes
  loadConversation()

  // Load messages for new conversation
  apiMessages.value = []
  await loadMessages()

  // Mark conversation as read (after messages are loaded)
  markConversationAsRead()

  // Auto-focus on textarea
  nextTick(() => {
    messageTextarea.value?.focus()
  })
})

onMounted(async () => {
  // Disable fixed body so iOS safe-area env() works
  document.body.classList.add('no-fixed-body')

  // Calculate dynamic messages container height for mobile
  calculateMessagesHeight()

  // Load conversations list
  loadConversationsList()

  // Load conversation on mount
  loadConversation()

  // Load messages
  await loadMessages()

  // Mark conversation as read (after messages are loaded)
  markConversationAsRead()

  // Auto-focus on textarea
  nextTick(() => {
    messageTextarea.value?.focus()
  })

  document.addEventListener('click', handleClickOutside)
  document.addEventListener('keydown', handleGalleryKeydown)

  // Listen for message.sent forwarded from AppNavigation via custom event
  window.addEventListener('echoMessageSent', handleEchoMessageSent as EventListener)
})

// Handle message.sent forwarded from AppNavigation
const handleEchoMessageSent = (event: CustomEvent) => {
  const data = event.detail
  if (!data) return

  if (data.conversationUuid === props.conversationUuid) {
    // Extract sender name parts
    const nameParts = data.senderName.split(' ')
    const firstName = nameParts[0] || ''
    const lastName = nameParts.slice(1).join(' ') || ''

    // Determine message type based on presence of images or document
    let messageType = 'text'
    if (data.imageUrls && data.imageUrls.length > 0) messageType = 'image'
    if (data.documentData) messageType = 'document'

    // Add message to the conversation
    const newMessage = {
      uuid: `pusher-${Date.now()}`,
      sender_person_uuid: data.senderUuid,
      sender_first_name: firstName,
      sender_last_name: lastName,
      message_type: messageType,
      content: data.lastMessage,
      created_at: data.lastMessageAt,
      is_deleted: false,
      imageUrls: data.imageUrls || [],
      documentUrl: data.documentData?.file_url,
      documentName: data.documentData?.file_name,
      documentSize: data.documentData?.file_size,
      documentExtension: data.documentData?.file_extension
    }

    apiMessages.value.push(newMessage)

    // Scroll to bottom
    nextTick(() => {
      scrollToBottom()
    })
  }
}

onBeforeUnmount(() => {
  document.body.classList.remove('no-fixed-body')
  document.removeEventListener('click', handleClickOutside)
  document.removeEventListener('keydown', handleGalleryKeydown)
  window.removeEventListener('echoMessageSent', handleEchoMessageSent as EventListener)
})
</script>

<template>
  <div class="w-full h-full flex" :class="showSidebar ? '' : ''">
    <!-- Left Sidebar - Conversations List (hidden when showSidebar is false, hidden below 1024px) -->
    <div v-if="showSidebar" class="hidden lg:flex lg:w-1/4 lg:min-w-[300px] bg-white border-r-2 border-gray-300 flex-col">
        <!-- Header -->
        <div class="flex items-center justify-between px-4 py-4 border-b-2 border-gray-300">
          <h2 class="text-xl font-bold text-gray-900">{{ t('conversations.title') }}</h2>
          <button
            @click="openNewConversationModal"
            class="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors cursor-pointer"
            :title="t('conversations.newConversation')"
          >
            <span class="text-white text-2xl leading-none">+</span>
          </button>
        </div>

        <!-- Conversations List -->
        <div class="flex-1 overflow-y-auto">
          <div
            v-for="conv in allConversations"
            :key="conv.uuid"
            @click="openConversation(conv.uuid)"
            :class="[
              'block px-4 py-3 border-b border-gray-200 hover:bg-gray-100 cursor-pointer transition-colors',
              conv.uuid === props.conversationUuid ? 'bg-blue-100' : (conv.is_unread ? 'bg-blue-50' : '')
            ]"
          >
            <div class="flex items-start gap-3">
              <div :class="['w-12 h-12 rounded-full flex-shrink-0 flex items-center justify-center text-white font-semibold', getAvatarClass(conv.is_group)]">
                {{ getAvatarContent(conv.name, conv.is_group) }}
              </div>
              <div class="flex-1 min-w-0">
                <div class="flex items-center justify-between mb-1">
                  <h3 :class="['text-sm text-gray-900 truncate', conv.is_unread ? 'font-bold' : 'font-semibold']">{{ conv.name }}</h3>
                  <span class="text-xs text-gray-500 ml-2 flex-shrink-0">{{ conv.time }}</span>
                </div>
                <p :class="['text-sm text-gray-600 truncate', conv.is_unread ? 'font-semibold' : '']">{{ conv.lastMessage }}</p>
              </div>
              <div v-if="conv.is_unread" class="w-3 h-3 bg-blue-600 rounded-full flex-shrink-0 mt-1"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Conversation Area -->
      <div class="flex-1 flex flex-col bg-white overflow-x-hidden relative min-h-0">
        <!-- Conversation Header -->
        <div class="flex items-center gap-2 min-[550px]:gap-3 px-2 min-[550px]:px-4 py-3 border-b-2 border-gray-300 bg-white flex-shrink-0">
          <!-- Back button (mobile only, hidden above 1024px) -->
          <button
            @click="goBack"
            class="lg:hidden w-8 h-8 min-[550px]:w-10 min-[550px]:h-10 flex items-center justify-center hover:bg-gray-100 rounded-full flex-shrink-0"
          >
            <svg class="w-5 h-5 min-[550px]:w-6 min-[550px]:h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
            </svg>
          </button>

          <!-- Loading state -->
          <div v-if="isLoadingConversation" class="flex-1 flex items-center justify-center">
            <div class="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          </div>

          <!-- Conversation header content -->
          <template v-else-if="currentConversation">
            <div :class="['w-8 h-8 min-[550px]:w-10 min-[550px]:h-10 text-xs min-[550px]:text-base rounded-full flex-shrink-0 flex items-center justify-center text-white font-semibold', getAvatarClass(currentConversation.is_group || false)]">
              {{ getAvatarContent(currentConversation.name || '', currentConversation.is_group || false) }}
            </div>

            <div class="flex-1 min-w-0">
              <h3 class="text-sm min-[550px]:text-lg font-semibold text-gray-900 truncate">{{ currentConversation.name }}</h3>
              <p v-if="currentConversation.is_group && !currentConversation.is_category_conversation" class="text-xs text-gray-500">
                {{ t('conversation.participantsCount', { count: participants.length }) }}
              </p>
            </div>

            <!-- More options -->
            <div v-if="!currentConversation?.is_system" class="relative options-menu flex-shrink-0">
              <button @click.stop="toggleOptionsMenu" class="w-8 h-8 min-[550px]:w-10 min-[550px]:h-10 flex items-center justify-center hover:bg-gray-100 rounded-full cursor-pointer">
                <svg class="w-5 h-5 min-[550px]:w-6 min-[550px]:h-6 text-gray-700" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z"/>
                </svg>
              </button>
              <div v-show="showOptionsMenu" class="absolute right-0 mt-2 w-56 bg-white border-2 border-gray-300 rounded shadow-lg z-50">
                <template v-if="currentConversation.is_group">
                  <button @click="openParticipantsModal" class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2 cursor-pointer">
                    <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"/></svg>
                    {{ t('conversation.participants') }}
                  </button>
                  <template v-if="!currentConversation.is_category_conversation">
                    <button @click="openAddParticipantsModal" class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2 cursor-pointer">
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path d="M8 9a3 3 0 100-6 3 3 0 000 6zM8 11a6 6 0 016 6H2a6 6 0 016-6zM16 7a1 1 0 10-2 0v1h-1a1 1 0 100 2h1v1a1 1 0 102 0v-1h1a1 1 0 100-2h-1V7z"/></svg>
                      {{ t('conversation.addParticipants') }}
                    </button>
                    <button @click="openEditGroupNameModal" class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2 cursor-pointer">
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"/></svg>
                      {{ t('conversation.editGroupName') }}
                    </button>
                  </template>
                </template>
                <button @click="openPhotosGallery" class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2 cursor-pointer">
                  <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clip-rule="evenodd"/></svg>
                  {{ t('conversation.photos') }}
                </button>
                <button @click="openDocumentsList" class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2 cursor-pointer">
                  <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clip-rule="evenodd"/></svg>
                  {{ t('conversation.documents') }}
                </button>
              </div>
            </div>
          </template>
        </div>

        <!-- Messages Container: on mobile fixed height, on desktop flex-1 -->
        <div ref="messagesContainer" class="overflow-y-auto overflow-x-hidden px-2 min-[550px]:px-4 py-4 bg-gray-50 md:flex-1" :style="messagesContainerHeight ? { height: messagesContainerHeight } : {}" @scroll="handleMessagesScroll">
          <!-- Loading older messages indicator -->
          <div v-if="isLoadingOlderMessages" class="flex items-center justify-center py-4">
            <div class="flex items-center gap-2 text-gray-600">
              <svg class="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span class="text-sm">{{ t('conversation.loadingOlder') }}</span>
            </div>
          </div>

          <div class="space-y-1">
            <div
              v-for="(msg, index) in messages"
              :key="index"
              :class="['flex', msg.isMe ? 'justify-end' : 'justify-start']"
            >
              <div class="max-w-[85%] min-[550px]:max-w-[70%]">
                <!-- Sender info (for received messages) -->
                <div v-if="!msg.isMe" class="flex items-center gap-2 mb-1">
                  <div class="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-semibold">
                    {{ msg.sender.charAt(0).toUpperCase() }}
                  </div>
                  <span class="text-xs font-semibold text-gray-700">{{ msg.sender }}</span>
                </div>

                <!-- Text Message -->
                <div
                  v-if="msg.type === 'text'"
                  :class="[
                    'rounded-2xl px-3 min-[550px]:px-4 py-2',
                    msg.isMe ? 'bg-blue-600 text-white' : 'bg-white border-2 border-gray-200 text-gray-900'
                  ]"
                >
                  <p :class="['text-sm break-words', msg.isMe ? 'link-white' : 'link-blue']" v-html="linkifyText(msg.message)"></p>
                  <div class="flex justify-end mt-0.5">
                    <span :class="['text-[10px]', msg.isMe ? 'text-white/70' : 'text-gray-400']">{{ msg.time }}</span>
                  </div>
                </div>

                <!-- Image Message -->
                <div
                  v-else-if="msg.type === 'image'"
                  :class="[
                    'rounded-2xl overflow-hidden',
                    msg.isMe ? 'bg-blue-600' : 'bg-white border-2 border-gray-200'
                  ]"
                >
                  <p v-if="msg.message" :class="['text-sm px-3 min-[550px]:px-4 pt-3 pb-2 break-words', msg.isMe ? 'text-white link-white' : 'text-gray-900 link-blue']" v-html="linkifyText(msg.message)"></p>
                  <div v-if="msg.imageUrls && msg.imageUrls.length > 0" class="px-2 min-[550px]:px-3 pb-3" :class="{'pt-3': !msg.message}">
                    <div class="flex flex-wrap gap-2">
                      <div
                        v-for="(imageUrl, index) in msg.imageUrls"
                        :key="index"
                        @click="!currentConversation?.is_system && openPhotosGalleryWithImage(imageUrl)"
                        :class="['block', !currentConversation?.is_system && 'cursor-pointer']"
                      >
                        <img
                          :src="imageUrl"
                          :alt="t('conversation.imageAlt', { index: index + 1 })"
                          :class="['max-w-[150px] min-[550px]:max-w-[200px] max-h-[150px] min-[550px]:max-h-[200px] rounded-lg object-cover', !currentConversation?.is_system && 'cursor-pointer hover:opacity-90 transition-opacity']"
                        />
                      </div>
                    </div>
                  </div>
                  <div class="px-3 min-[550px]:px-4 pb-3 pt-1">
                    <div class="flex justify-end">
                      <span :class="['text-[10px]', msg.isMe ? 'text-white/70' : 'text-gray-400']">{{ msg.time }}</span>
                    </div>
                  </div>
                </div>

                <!-- Document Message -->
                <div
                  v-else-if="msg.type === 'document'"
                  :class="[
                    'rounded-2xl px-3 min-[550px]:px-4 py-3',
                    msg.isMe ? 'bg-blue-600 text-white' : 'bg-white border-2 border-gray-200 text-gray-900'
                  ]"
                >
                  <p v-if="msg.message" :class="['text-sm mb-2 break-words', msg.isMe ? 'link-white' : 'link-blue']" v-html="linkifyText(msg.message)"></p>
                  <div
                    v-if="msg.documentAttachment"
                    :class="[
                      'flex items-center gap-3 p-3 rounded-lg',
                      !currentConversation?.is_system && 'cursor-pointer hover:opacity-90 transition-opacity',
                      currentConversation?.is_system && 'cursor-default',
                      msg.isMe ? 'bg-blue-700' : 'bg-gray-50'
                    ]"
                    @click="!currentConversation?.is_system && downloadDocument(msg.documentAttachment.file_url, msg.documentAttachment.file_name)"
                  >
                    <!-- Document Icon -->
                    <div class="flex-shrink-0">
                      <svg
                        :class="['w-10 h-10', msg.isMe ? 'text-white' : 'text-blue-600']"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path fill-rule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clip-rule="evenodd"/>
                      </svg>
                    </div>
                    <!-- Document Info -->
                    <div class="flex-1 min-w-0">
                      <p :class="[
                        'text-sm font-semibold truncate',
                        msg.isMe ? 'text-white' : 'text-gray-900'
                      ]">
                        {{ msg.documentAttachment.file_name }}
                      </p>
                      <p :class="[
                        'text-xs',
                        msg.isMe ? 'text-blue-100' : 'text-gray-500'
                      ]">
                        {{ msg.documentAttachment.file_extension?.toUpperCase() }} • {{ (msg.documentAttachment.file_size / 1024 / 1024).toFixed(2) }} MB
                      </p>
                    </div>
                    <!-- Download Icon -->
                    <svg
                      :class="['w-5 h-5 flex-shrink-0', msg.isMe ? 'text-white' : 'text-gray-600']"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path fill-rule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clip-rule="evenodd"/>
                    </svg>
                  </div>
                  <div class="flex justify-end mt-0.5">
                    <span :class="['text-[10px]', msg.isMe ? 'text-white/70' : 'text-gray-400']">{{ msg.time }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Message Input Bar - in normal flow -->
        <div v-if="!currentConversation?.is_system" class="px-2 min-[550px]:px-4 py-3 border-t-2 border-gray-300 bg-white flex-shrink-0">
          <!-- Error message -->
          <div v-if="messageSendError" class="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p class="text-sm text-red-600">{{ messageSendError }}</p>
          </div>

          <!-- Document Preview Container -->
          <div v-if="selectedDocument" class="mb-3 p-3 bg-gray-50 rounded-lg border-2 border-gray-200">
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2">
                <svg class="w-8 h-8 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clip-rule="evenodd"/>
                </svg>
                <div>
                  <p class="text-sm font-semibold text-gray-700">{{ selectedDocument.name }}</p>
                  <p class="text-xs text-gray-500">{{ (selectedDocument.file.size / 1024 / 1024).toFixed(2) }} MB</p>
                </div>
              </div>
              <button
                type="button"
                @click="removeDocument"
                class="w-8 h-8 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors flex items-center justify-center"
              >
                <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
                </svg>
              </button>
            </div>
          </div>

          <!-- Image Preview Container -->
          <div v-if="selectedImages.length > 0" class="mb-3 p-2 bg-gray-50 rounded-lg border-2 border-gray-200">
            <div class="flex items-center justify-between mb-2">
              <span class="text-xs font-semibold text-gray-700">{{ t('conversation.selectedImagesCount', { count: selectedImages.length }) }}</span>
              <button
                type="button"
                @click="clearAllImages"
                class="text-xs text-red-600 hover:text-red-700 font-medium"
              >
                {{ t('conversation.removeAllImages') }}
              </button>
            </div>
            <div class="flex flex-wrap gap-2">
              <div v-for="(image, index) in selectedImages" :key="index" class="relative group">
                <img
                  :src="image.url"
                  :alt="`Preview ${index + 1}`"
                  class="w-20 h-20 object-cover rounded-lg border-2 border-gray-300"
                />
                <button
                  type="button"
                  @click="removeImage(index)"
                  class="absolute -top-2 -right-2 w-6 h-6 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors opacity-0 group-hover:opacity-100 flex items-center justify-center"
                >
                  <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fill-rule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          <!-- Hidden file inputs -->
          <input
            ref="imageInput"
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/heic,image/heif"
            multiple
            class="hidden"
            @change="handleImageSelect"
          />
          <input
            ref="documentInput"
            type="file"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.xml,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/xml,application/xml"
            class="hidden"
            @change="handleDocumentSelect"
          />

          <form @submit.prevent="sendMessage" class="flex items-center gap-1.5 min-[550px]:gap-2">
            <!-- Mobile: Plus button (when input focused) OR attachment buttons (when not focused) -->
            <!-- Plus button - shown on mobile when input is focused -->
            <button
              v-if="isInputFocused"
              type="button"
              @click="handlePlusClick"
              class="md:hidden w-10 h-10 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
            >
              <svg class="w-7 h-7 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
              </svg>
            </button>

            <!-- Attachment buttons - on mobile: shown when input NOT focused; on desktop: always shown -->
            <div :class="[isInputFocused ? 'hidden md:flex' : 'flex']" class="gap-1 min-[550px]:gap-2 flex-shrink-0">
              <button
                type="button"
                @click="documentInput?.click()"
                class="w-10 h-10 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
                :title="t('conversation.addDocument')"
              >
                <svg class="w-6 h-6 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clip-rule="evenodd"/>
                </svg>
              </button>
              <button
                type="button"
                @click="imageInput?.click()"
                class="w-10 h-10 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
                :title="t('conversation.addPhoto')"
              >
                <svg class="w-6 h-6 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clip-rule="evenodd"/>
                </svg>
              </button>
            </div>

            <!-- Message input -->
            <textarea
              ref="messageTextarea"
              v-model="messageInput"
              @input="handleMessageInput"
              @keydown="handleMessageKeydown"
              @focus="handleInputFocus"
              @blur="handleInputBlur"
              :placeholder="t('conversation.messagePlaceholder')"
              rows="1"
              class="flex-1 px-3 min-[550px]:px-4 py-2 border-2 border-gray-300 rounded-2xl focus:outline-none focus:border-blue-600 text-sm resize-none"
              style="min-height: 40px; max-height: 72px; overflow-y: hidden;"
            ></textarea>

            <!-- Send button -->
            <button
              type="submit"
              class="px-4 min-[550px]:px-6 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors font-medium text-sm flex-shrink-0"
            >
              {{ t('conversation.send') }}
            </button>
          </form>
        </div>
      </div>
    </div>

  <!-- NEW CONVERSATION MODAL - skopiowany z ConversationsView -->
  <div
    v-if="showNewConversationModal"
    class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
    @click.self="closeNewConversationModal"
  >
    <div class="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[80vh] flex flex-col">
      <div class="flex items-center justify-between px-6 py-4 border-b-2 border-gray-300">
        <h2 class="text-xl font-bold text-gray-900">{{ t('conversations.newConversationTitle') }}</h2>
        <button @click="closeNewConversationModal" class="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors">
          <svg class="w-5 h-5 text-gray-700" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
          </svg>
        </button>
      </div>
      <div class="px-6 py-4 border-b border-gray-200">
        <div class="relative">
          <input v-model="newConvSearchQuery" type="text" :placeholder="t('conversations.searchPerson')" class="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-600" />
          <svg class="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clip-rule="evenodd"/>
          </svg>
        </div>
      </div>
      <div class="flex-1 overflow-y-auto px-6 py-4">
        <div v-for="user in filteredNewConvUsers" :key="user.id" @click="toggleNewConvUserSelection(user.id)" :class="['flex items-center gap-3 p-3 border-2 border-gray-200 rounded-lg mb-3 cursor-pointer hover:bg-gray-50 transition-colors', isNewConvUserSelected(user.id) ? 'bg-blue-50 border-blue-600' : '']">
          <input type="checkbox" :checked="isNewConvUserSelected(user.id)" class="w-5 h-5 text-blue-600 rounded border-gray-300 focus:ring-blue-600" @click.stop="toggleNewConvUserSelection(user.id)" />
          <div class="w-10 h-10 rounded-full bg-blue-500 flex-shrink-0 flex items-center justify-center text-white font-semibold">{{ user.name.charAt(0).toUpperCase() }}</div>
          <div class="flex-1 min-w-0">
            <p class="text-sm font-semibold text-gray-900 truncate">{{ user.name }}</p>
            <p class="text-xs text-gray-500 truncate">{{ user.role }}</p>
          </div>
        </div>
        <div v-if="filteredNewConvUsers.length === 0" class="text-center py-8">
          <p class="text-gray-500">{{ t('conversation.noUsersFound') }}</p>
        </div>
      </div>
      <div v-if="newConvSelectedUsers.length > 1" class="px-6 py-4 border-t border-gray-200">
        <label class="block text-sm font-semibold text-gray-900 mb-2">{{ t('conversation.groupName') }}</label>
        <input v-model="newConvGroupName" type="text" :placeholder="t('conversation.groupNamePlaceholder')" @input="handleNewConvGroupNameInput" :class="['w-full px-4 py-2 border-2 rounded-lg focus:outline-none', newConvGroupNameError ? 'border-red-600 focus:border-red-600' : 'border-gray-300 focus:border-blue-600']" />
        <p v-if="newConvGroupNameError" class="text-xs text-red-600 mt-1">{{ t('conversation.groupNameRequired') }}</p>
      </div>
      <div class="flex items-center justify-between px-6 py-4 border-t-2 border-gray-300">
        <p class="text-sm text-gray-600">{{ t('conversation.selectedPeople', { count: newConvSelectedUsers.length }) }}</p>
        <div class="flex gap-2">
          <button @click="closeNewConversationModal" class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors">{{ t('conversation.cancel') }}</button>
          <button @click="createNewConversation" :disabled="newConvSelectedUsers.length === 0" class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed">
            {{ newConvSelectedUsers.length > 1 ? t('conversation.createGroup') : t('conversation.startConversation') }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- PARTICIPANTS MODAL -->
  <div v-if="showParticipantsModal" class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" @click.self="closeParticipantsModal">
    <div class="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[80vh] flex flex-col">
      <div class="flex items-center justify-between px-6 py-4 border-b-2 border-gray-300">
        <h2 class="text-xl font-bold text-gray-900">{{ t('conversation.participants') }} ({{ participantsList.length }})</h2>
        <button @click="closeParticipantsModal" class="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors">
          <svg class="w-5 h-5 text-gray-700" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
          </svg>
        </button>
      </div>
      <div class="flex-1 overflow-y-auto px-6 py-4">
        <!-- Error message -->
        <div v-if="participantsError" class="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p class="text-sm text-red-600">{{ participantsError }}</p>
        </div>

        <!-- Loading state -->
        <div v-if="isLoadingParticipants" class="flex items-center justify-center py-8">
          <div class="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
        </div>

        <!-- Participants list -->
        <div v-else class="space-y-3">
          <div v-for="participant in participantsList" :key="participant.uuid" class="flex items-center gap-3 p-2 hover:bg-gray-50 rounded transition-colors">
            <div class="w-12 h-12 rounded-full bg-blue-500 flex-shrink-0 flex items-center justify-center text-white font-semibold">
              {{ participant.first_name.charAt(0).toUpperCase() }}
            </div>
            <div class="flex-1">
              <div class="flex items-center gap-2">
                <h3 class="text-sm font-semibold text-gray-900">
                  {{ participant.first_name }} {{ participant.last_name }}
                  <span v-if="isCurrentUser(participant.person_uuid)" class="text-xs font-normal text-gray-500">{{ t('conversation.you') }}</span>
                </h3>
              </div>
              <p class="text-xs text-gray-500">{{ getRoleTranslation(participant.role) }}</p>
            </div>
            <span v-if="participant.is_admin" class="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded">{{ t('conversation.admin') }}</span>
            <button
              v-if="isCurrentUserAdmin && !isCurrentUser(participant.person_uuid) && !participant.is_admin"
              @click="confirmRemoveParticipant(participant.uuid, `${participant.first_name} ${participant.last_name}`)"
              class="px-3 py-1 bg-red-100 text-red-700 text-xs font-medium rounded hover:bg-red-200 transition-colors"
            >
              {{ t('conversation.removeParticipant') }}
            </button>
          </div>

          <!-- Empty state -->
          <div v-if="participantsList.length === 0 && !isLoadingParticipants" class="text-center py-8">
            <p class="text-gray-500">{{ t('conversation.noParticipants') }}</p>
          </div>
        </div>
      </div>
      <div class="px-6 py-4 border-t-2 border-gray-300">
        <button @click="closeParticipantsModal" class="w-full px-4 py-2 bg-gray-100 text-sm font-medium text-gray-700 rounded hover:bg-gray-200 border-2 border-gray-300">{{ t('conversation.closeParticipants') }}</button>
      </div>
    </div>
  </div>

  <!-- ADD PARTICIPANTS MODAL (dla grup) -->
  <div v-if="showAddParticipantsModal" class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" @click.self="closeAddParticipantsModal">
    <div class="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[80vh] flex flex-col">
      <div class="flex items-center justify-between px-6 py-4 border-b-2 border-gray-300">
        <h2 class="text-xl font-bold text-gray-900">{{ t('conversation.addParticipantsTitle') }}</h2>
        <button @click="closeAddParticipantsModal" class="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors">
          <svg class="w-5 h-5 text-gray-700" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
          </svg>
        </button>
      </div>
      <div class="px-6 py-4 border-b border-gray-200">
        <input
          v-model="addParticipantsSearchQuery"
          type="text"
          :placeholder="t('conversation.searchPersonAdd')"
          class="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-600 text-sm"
        />
      </div>
      <div class="flex-1 overflow-y-auto px-6 py-4">
        <!-- Error message -->
        <div v-if="addParticipantsError" class="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p class="text-sm text-red-600">{{ addParticipantsError }}</p>
        </div>

        <!-- Loading spinner -->
        <div v-if="isLoadingAvailablePersons" class="flex items-center justify-center py-8">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>

        <!-- No available persons -->
        <div v-else-if="filteredAvailablePersons.length === 0" class="text-center py-8">
          <p class="text-sm text-gray-500">
            {{ addParticipantsSearchQuery ? t('conversation.noPersonsSearch') : t('conversation.noPersonsEmpty') }}
          </p>
        </div>

        <!-- Available persons list -->
        <div v-else class="space-y-2">
          <div
            v-for="person in filteredAvailablePersons"
            :key="person.uuid"
            @click="togglePersonSelectionForAdd(person.uuid)"
            class="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors"
          >
            <input
              type="checkbox"
              :checked="isPersonSelectedForAdd(person.uuid)"
              @click.stop="togglePersonSelectionForAdd(person.uuid)"
              class="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <div class="w-10 h-10 rounded-full bg-blue-500 flex-shrink-0 flex items-center justify-center text-white font-semibold text-sm">
              {{ person.first_name.charAt(0).toUpperCase() }}
            </div>
            <div class="flex-1 min-w-0">
              <h3 class="text-sm font-semibold text-gray-900 truncate">{{ person.first_name }} {{ person.last_name }}</h3>
              <p class="text-xs text-gray-500 truncate">{{ getRoleTranslation(person.role) }}</p>
            </div>
          </div>
        </div>
      </div>
      <div class="px-6 py-4 border-t-2 border-gray-300">
        <div class="flex gap-2">
          <button @click="closeAddParticipantsModal" class="flex-1 px-4 py-2 bg-gray-100 text-sm font-medium text-gray-700 rounded hover:bg-gray-200 border-2 border-gray-300">{{ t('conversation.cancel') }}</button>
          <button
            @click="addParticipantsToGroup"
            :disabled="addParticipantsSelectedUsers.length === 0 || isAddingParticipants"
            class="flex-1 px-4 py-2 bg-blue-600 text-sm font-medium text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {{ isAddingParticipants ? t('conversation.adding') : t('conversation.addCount', { count: addParticipantsSelectedUsers.length }) }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- EDIT GROUP NAME MODAL -->
  <div v-if="showEditGroupNameModal" class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" @click.self="closeEditGroupNameModal">
    <div class="bg-white rounded-lg shadow-xl max-w-md w-full">
      <div class="p-6">
        <h2 class="text-xl font-bold text-gray-900 mb-4">{{ t('conversation.editGroupNameLabel') }}</h2>

        <!-- Error message -->
        <div v-if="editGroupNameError" class="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p class="text-sm text-red-600">{{ editGroupNameError }}</p>
        </div>

        <div class="mb-6">
          <label class="block text-sm font-semibold text-gray-700 mb-2">{{ t('conversation.newGroupName') }}</label>
          <input
            v-model="editGroupName"
            type="text"
            :placeholder="t('conversation.groupNamePlaceholder')"
            class="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-600 text-sm"
            @input="editGroupNameError = ''"
          />
        </div>
        <div class="flex gap-3 justify-end">
          <button @click="closeEditGroupNameModal" class="px-4 py-2 bg-gray-100 text-sm font-medium text-gray-700 rounded hover:bg-gray-200 border-2 border-gray-300">{{ t('conversation.cancel') }}</button>
          <button
            @click="saveGroupName"
            :disabled="isSavingGroupName"
            class="px-4 py-2 bg-blue-600 text-sm font-medium text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {{ isSavingGroupName ? t('conversation.saving') : t('conversation.saveGroupName') }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- PHOTOS GALLERY MODAL -->
  <div v-if="showPhotosGalleryModal" class="fixed inset-0 bg-black bg-opacity-95 z-50 flex items-center justify-center">
    <div class="w-full h-full flex flex-col">
      <div class="flex items-center justify-between px-6 py-4 mt-[30px] md:mt-0 bg-black bg-opacity-50">
        <h2 class="text-xl font-bold text-white">{{ t('conversation.photosFromConversation') }}</h2>
        <button @click="closePhotosGallery" class="w-10 h-10 flex items-center justify-center hover:bg-white hover:bg-opacity-20 rounded-full transition-colors">
          <svg class="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
          </svg>
        </button>
      </div>
      <div class="flex-1 flex items-center justify-center relative px-16">
        <button @click="previousPhoto" class="absolute left-4 w-12 h-12 bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full flex items-center justify-center transition-colors">
          <svg class="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clip-rule="evenodd"/>
          </svg>
        </button>
        <div class="max-w-4xl max-h-full flex items-center justify-center">
          <div v-if="isLoadingPhotos" class="text-white text-lg">{{ t('conversation.loadingPhotos') }}</div>
          <div v-else-if="conversationPhotos.length === 0" class="text-white text-lg">{{ t('conversation.noPhotos') }}</div>
          <img v-else :src="conversationPhotos[currentPhotoIndex]?.url" :alt="conversationPhotos[currentPhotoIndex]?.name" class="max-w-full max-h-[70vh] object-contain rounded-lg" />
        </div>
        <button @click="nextPhoto" class="absolute right-4 w-12 h-12 bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full flex items-center justify-center transition-colors">
          <svg class="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd"/>
          </svg>
        </button>
      </div>
      <div class="px-6 py-4 bg-black bg-opacity-50">
        <div ref="thumbnailContainer" class="flex gap-2 overflow-x-auto pb-2 flex-row-reverse justify-center scroll-smooth scrollbar-hide" style="-webkit-overflow-scrolling: touch;">
          <img
            v-for="(photo, index) in conversationPhotos"
            :key="index"
            :ref="el => { if (index === currentPhotoIndex) currentThumbnail = el as HTMLElement }"
            :src="photo.thumb"
            :alt="photo.name"
            @click="goToPhoto(index)"
            :class="['w-16 h-16 object-cover rounded cursor-pointer transition-all', currentPhotoIndex === index ? 'ring-4 ring-blue-500 opacity-100' : 'opacity-60 hover:opacity-100']"
            :style="{ marginLeft: getThumbnailMargin(index) }"
          />
        </div>
      </div>
    </div>
  </div>

  <!-- DOCUMENTS LIST MODAL -->
  <div v-if="showDocumentsListModal" class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" @click.self="closeDocumentsList">
    <div class="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col">
      <div class="flex items-center justify-between px-6 py-4 border-b-2 border-gray-300">
        <h2 class="text-xl font-bold text-gray-900">{{ t('conversation.documentsFromConversation') }}</h2>
        <button @click="closeDocumentsList" class="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors">
          <svg class="w-5 h-5 text-gray-700" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
          </svg>
        </button>
      </div>
      <div class="flex-1 overflow-y-auto px-6 py-4" @scroll="handleDocumentsScroll">
        <div class="space-y-3">
          <div
            v-for="(doc, index) in displayedDocuments"
            :key="doc.uuid"
            class="flex items-center gap-3 p-4 border-2 border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
            @click="downloadDocument(doc.file_url, doc.file_name)"
          >
            <svg :class="['w-10 h-10 flex-shrink-0', doc.file_extension === 'pdf' ? 'text-red-600' : (doc.file_extension === 'docx' || doc.file_extension === 'doc') ? 'text-blue-600' : 'text-green-600']" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clip-rule="evenodd"/>
            </svg>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-semibold text-gray-900 truncate">{{ doc.file_name }}</p>
              <p class="text-xs text-gray-500">{{ doc.file_extension?.toUpperCase() }} • {{ (doc.file_size / 1024 / 1024).toFixed(2) }} MB • {{ formatDocumentDate(doc.uploaded_at) }}</p>
            </div>
            <svg class="w-5 h-5 text-blue-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clip-rule="evenodd"/>
            </svg>
          </div>
        </div>
        <div v-if="isLoadingDocuments" class="text-center py-4">
          <div class="inline-block w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <p class="text-sm text-gray-500 mt-2">{{ t('conversation.loadingDocuments') }}</p>
        </div>
      </div>
    </div>
  </div>

  <!-- REMOVE PARTICIPANT CONFIRMATION MODAL -->
  <div v-if="showRemoveParticipantModal" class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" @click.self="closeRemoveParticipantModal">
    <div class="bg-white rounded-lg shadow-xl max-w-md w-full">
      <div class="p-6">
        <h2 class="text-xl font-bold text-gray-900 mb-4">{{ t('conversation.confirmRemoveTitle') }}</h2>
        <p class="text-gray-700 mb-6">
          {{ t('conversation.confirmRemoveMessage', { name: participantToRemove?.name }) }}
        </p>
        <div class="flex gap-3 justify-end">
          <button
            @click="closeRemoveParticipantModal"
            :disabled="isRemovingParticipant"
            class="px-4 py-2 bg-gray-100 text-sm font-medium text-gray-700 rounded hover:bg-gray-200 border-2 border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {{ t('conversation.cancel') }}
          </button>
          <button
            @click="removeParticipant"
            :disabled="isRemovingParticipant"
            class="px-4 py-2 bg-red-600 text-sm font-medium text-white rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <div v-if="isRemovingParticipant" class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            <span>{{ isRemovingParticipant ? t('conversation.removing') : t('conversation.removeParticipant') }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Link colors for messages */
.link-white :deep(a) {
  color: white;
}

.link-blue :deep(a) {
  color: #3b82f6;
}
</style>
